import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatDateFR, formatApiError, photoUrl } from "../../lib/api";
import { emitDataChanged } from "../../lib/dataBus";
import { toast } from "sonner";
import { confirmAction } from "../../components/ConfirmDialog";
import { Plus, X, Pencil, Trash2, Power, MapPin, Hash, TrendingUp, Calendar as CalIcon, Upload, Star, Search, Eye, LayoutGrid, List, RotateCcw, Archive, ShieldAlert, CarFront, Loader2, FileText, Settings, Info, ChevronDown } from "lucide-react";
import { usePermissions } from "../../lib/permissions";
import { orderedActiveFields } from "../../components/fleet/fieldConfig";
import { vehiclePrimaryLabel } from "../../lib/vehicleLabel";
import TabToolbar from "../../components/pilotage/TabToolbar";
import ViewToggle from "../../components/pilotage/ViewToggle";
import { FilterDropdown } from "../../components/pilotage/Filters";
import EmptyState from "../../components/pilotage/EmptyState";
import { SkeletonCards, SkeletonTableRows } from "../../components/ui/Skeleton";
import { CsvMenu } from "../../components/pilotage/CsvActions";

const FUELS = ["Essence", "Diesel", "Hybride", "Électrique"];
const TRANS = ["Automatique", "Manuelle"];
const DEFAULT_STATUSES = [
  { value: "active", label: "Disponible" },
  { value: "rented", label: "Loué" },
  { value: "reserved", label: "Réservé" },
  { value: "cleaning", label: "Nettoyage" },
  { value: "maintenance", label: "Maintenance" },
  { value: "out_of_service", label: "Hors service" },
  { value: "sold", label: "Vendu" },
  { value: "inactive", label: "Inactif" },
];
const CRIT_AIR = ["1", "2", "3", "4", "5", "E"];
const CONDITIONS = [
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Bon" },
  { value: "fair", label: "Moyen" },
  { value: "poor", label: "Dégradé" },
];
const EQUIPMENT_OPTIONS = [
  ["gps", "GPS"],
  ["bluetooth", "Bluetooth"],
  ["carplay", "Apple CarPlay"],
  ["android_auto", "Android Auto"],
  ["air_conditioning", "Climatisation"],
  ["baby_seat", "Siège bébé"],
  ["snow_chains", "Chaînes neige"],
  ["winter_tires", "Pneus hiver"],
  ["pmr", "Accessibilité PMR"],
  ["pmr_ramp", "Rampe PMR"],
  ["rear_camera", "Caméra de recul"],
  ["parking_sensors", "Radar de recul"],
  ["cruise_control", "Régulateur de vitesse"],
];

const empty = {
  name: "", brand: "", model: "", version: "", year: "", internal_ref: "",
  city: "", description: "", photos: [],
  seats: 5, doors: 5, color: "",
  transmission: "Automatique", fuel: "Essence", category: "", vehicle_type: "car",
  status: "active", license_plate: "", vin: "", location_id: "", location_ids: [],
  fiscal_power: "", engine_power: "", mileage: 0, max_mileage: "",
  first_registration_date: "", fuel_level: 100, tank_capacity: "",
  autonomy: "", co2_emissions: "", crit_air: "",
  purchase_price: "", purchase_date: "", estimated_value: "",
  insurance_monthly: "", maintenance_estimated: "",
  deposit: 1500, deductible: "", km_included_daily: 200, price_extra_km: "",
  equipment: {}, condition_interior: "good", condition_exterior: "good",
  known_damages: "", internal_notes: "",
};

// Coerce null/undefined field values → "" for controlled inputs (avoid React warning
// when editing legacy vehicles missing the new Phase 1 fields).
const normalizeForm = (v) => {
  const out = { ...empty, ...v };
  Object.keys(empty).forEach((k) => {
    if (out[k] === null || out[k] === undefined) out[k] = empty[k];
  });
  out.equipment = { ...(v?.equipment || {}) };
  out.photos = v?.photos?.length ? v.photos : [];
  // Multi-agences : rétro-compat — un véhicule existant à une seule agence (location_id)
  // est converti en liste. On ne perd jamais l'agence actuelle.
  if (Array.isArray(v?.location_ids) && v.location_ids.length) out.location_ids = [...v.location_ids];
  else if (v?.location_id) out.location_ids = [v.location_id];
  else out.location_ids = [];
  return out;
};

function AgencyMultiSelect({ locations, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = value || [];
  const allSelected = locations.length > 0 && locations.every((l) => selected.includes(l.id));
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleAll = () => onChange(allSelected ? [] : locations.map((l) => l.id));
  const summary = allSelected
    ? "Toutes les agences"
    : selected.length === 0
      ? "— Sélectionner une ou plusieurs agences —"
      : selected.length === 1
        ? (locations.find((l) => l.id === selected[0])?.name || "1 agence")
        : `${selected.length} agences sélectionnées`;
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Agences<span className="text-red-500 ml-0.5">*</span></label>
      <div className="relative mt-1.5" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-sm text-left flex items-center justify-between hover:border-violet-400 focus:outline-none focus:border-violet-500"
          data-testid="v-locations-trigger"
        >
          <span className={`truncate ${selected.length === 0 ? "text-slate-400" : "text-slate-800"}`}>{summary}</span>
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        </button>
        {open && (
          <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg p-1.5 max-h-60 overflow-y-auto" data-testid="v-locations-menu">
            <label className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 cursor-pointer border-b border-slate-100 mb-1">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-violet-600" data-testid="v-location-all" />
              <span className="text-sm font-semibold text-slate-800">Toutes les agences</span>
            </label>
            {locations.map((l) => (
              <label key={l.id} className="flex items-center gap-2 px-2.5 py-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggle(l.id)} className="accent-violet-600" data-testid={`v-location-${l.id}`} />
                <span className="text-sm text-slate-700">{l.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 && !allSelected && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="v-locations-chips">
          {selected.map((id) => {
            const name = locations.find((l) => l.id === id)?.name || id;
            return (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
                {name}
                <button type="button" onClick={() => toggle(id)} className="hover:text-violet-900" data-testid={`v-location-chip-remove-${id}`}><X className="h-3 w-3" /></button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AVAIL = {
  available: { label: "Disponible", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  rented: { label: "Loué", color: "bg-violet-100 text-violet-700", dot: "bg-violet-500" },
  maintenance: { label: "Maintenance", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  inactive: { label: "Inactif", color: "bg-slate-200 text-slate-700", dot: "bg-slate-400" },
};

// Mapping tailwind pour les couleurs custom des statuts (Phase 3/5)
const STATUS_COLOR_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  red: "bg-red-100 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  teal: "bg-teal-100 text-teal-700 border-teal-200",
};
const statusBadgeClasses = (status, STATUSES) => {
  const s = STATUSES.find((x) => x.value === status);
  return STATUS_COLOR_CLASSES[s?.color] || STATUS_COLOR_CLASSES.slate;
};
const statusLabelFor = (status, STATUSES) => {
  const s = STATUSES.find((x) => x.value === status);
  return s?.label || status;
};

export default function VendorVehicles({ embedded = false }) {
  const { can } = usePermissions();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [statsMap, setStatsMap] = useState({});
  const [locations, setLocations] = useState([]);
  const [CATS, setCATS] = useState([]);
  const [CAT_OBJS, setCAT_OBJS] = useState([]);
  const [VTYPES, setVTYPES] = useState([]);
  const [VTYPES_FULL, setVTYPES_FULL] = useState([]);
  const [fieldCatalog, setFieldCatalog] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [quickCreate, setQuickCreate] = useState({ open: false, kind: "type", value: "" });
  const [quickSaving, setQuickSaving] = useState(false);
  const [STATUSES, setSTATUSES] = useState(DEFAULT_STATUSES);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("vehicles-view") || "cards");
  useEffect(() => { localStorage.setItem("vehicles-view", viewMode); }, [viewMode]);
  // Filtres & recherche
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [categoryF, setCategoryF] = useState("all");
  const [fuelF, setFuelF] = useState("all");
  const [transF, setTransF] = useState("all");
  const [agencyF, setAgencyF] = useState("all");
  const [sortBy, setSortBy] = useState("created_desc");

  const load = () => {
    setLoading(true);
    const params = { sort: sortBy };
    if (search) params.q = search;
    if (statusF === "archived") params.archived = 1;
    else if (statusF !== "all") params.status_f = statusF;
    if (categoryF !== "all") params.category = categoryF;
    if (typeF !== "all") params.vehicle_type = typeF;
    if (fuelF !== "all") params.fuel = fuelF;
    if (transF !== "all") params.transmission = transF;
    if (agencyF !== "all") params.location = agencyF;
    api.get("/vendor/vehicles", { params })
      .then((v) => {
        const vs = v.data.vehicles || [];
        setVehicles(vs);
        Promise.all(vs.map((x) => api.get(`/vendor/vehicles/${x.id}/stats`).then((r) => [x.id, r.data]).catch(() => null)))
          .then((pairs) => {
            const m = {};
            pairs.forEach((p) => { if (p) m[p[0]] = p[1]; });
            setStatsMap(m);
          });
      })
      .finally(() => setLoading(false));
  };
  // Recherge automatique quand filtres changent (debounced pour search)
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusF, typeF, categoryF, fuelF, transF, agencyF, sortBy]);

  // Charge les agences pour le select "Ville / Agence rattachée"
  useEffect(() => {
    api.get("/vendor/locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
    // Charge les catégories & statuts personnalisés (fallback aux défauts si erreur)
    api.get("/vendor/settings").then((r) => {
      const fleet = r.data?.fleet || {};
      if (Array.isArray(fleet.categories) && fleet.categories.length > 0) {
        setCATS(fleet.categories.map((c) => c.value));
      }
      setCAT_OBJS(Array.isArray(fleet.categories) ? fleet.categories : []);
      setVTYPES(Array.isArray(fleet.vehicle_types) ? fleet.vehicle_types.map((t) => ({ value: t.value, label: t.label })) : []);
      setVTYPES_FULL(Array.isArray(fleet.vehicle_types) ? fleet.vehicle_types : []);
      setFieldCatalog(r.data?.catalog?.field_catalog || []);
      if (Array.isArray(fleet.statuses) && fleet.statuses.length > 0) {
        setSTATUSES(fleet.statuses.map((s) => ({ value: s.value, label: s.label, color: s.color })));
      }
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, []);

  const openCreate = () => {
    setEditing(null);
    const firstType = VTYPES[0]?.value || "car";
    const firstCat = CAT_OBJS.find((c) => c.type === firstType)?.value || "";
    setForm(normalizeForm({ vehicle_type: firstType, category: firstCat }));
    setError("");
    setOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm(normalizeForm(v));
    setError("");
    setOpen(true);
  };

  // Création rapide d'un type de bien / catégorie sans quitter le formulaire véhicule (PUT settings autoritaire)
  const genId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
  const saveQuickCreate = async () => {
    const label = (quickCreate.value || "").trim();
    if (!label) { toast.error("Saisissez un nom"); return; }
    setQuickSaving(true);
    try {
      const { data } = await api.get("/vendor/settings");
      const fleet = data.fleet || {};
      const types = [...(fleet.vehicle_types || [])];
      const cats = [...(fleet.categories || [])];
      if (quickCreate.kind === "type") {
        if (types.some((t) => (t.label || "").trim().toLowerCase() === label.toLowerCase())) {
          toast.error("Un type de bien porte déjà ce nom"); setQuickSaving(false); return;
        }
        const id = genId("type");
        const stdCatId = genId("cat");
        types.push({ value: id, label, custom: true });
        // Lot 1 — tout nouveau type reçoit automatiquement sa catégorie « [Type] standard »
        cats.push({ value: stdCatId, label: `${label} standard`, type: id });
        await api.put("/vendor/settings", { fleet: { vehicle_types: types, categories: cats } });
        setVTYPES(types.map((t) => ({ value: t.value, label: t.label })));
        setCAT_OBJS(cats);
        setForm((f) => ({ ...f, vehicle_type: id, category: stdCatId }));
        toast.success(`Type « ${label} » créé avec sa catégorie standard`);
      } else {
        const type = form.vehicle_type;
        if (cats.some((c) => c.type === type && (c.label || "").trim().toLowerCase() === label.toLowerCase())) {
          toast.error("Une catégorie porte déjà ce nom pour ce type"); setQuickSaving(false); return;
        }
        const id = genId("cat");
        cats.push({ value: id, label, type });
        await api.put("/vendor/settings", { fleet: { vehicle_types: types, categories: cats } });
        setVTYPES(types.map((t) => ({ value: t.value, label: t.label })));
        setCAT_OBJS(cats);
        setForm((f) => ({ ...f, category: id }));
        toast.success(`Catégorie « ${label} » créée`);
      }
      setQuickCreate({ open: false, kind: "type", value: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setQuickSaving(false);
  };

  // Ouverture du formulaire d'édition via ?edit=<id> (depuis la fiche véhicule)
  const [searchParams, setSearchParams] = useSearchParams();

  // Filtres transmis par le Copilote via l'URL (?status=, ?q=)
  useEffect(() => {
    const s = searchParams.get("status");
    if (s && ["active", "rented", "maintenance", "inactive"].includes(s)) setStatusF(s);
    const q = searchParams.get("q");
    if (q && q.trim()) setSearch(q.trim().slice(0, 60));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.id === editParam);
    if (v) openEdit(v);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam, vehicles]);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    // Lot 1 — garde-fous d'intégrité côté client (Type → Catégorie → Agence)
    if (!form.vehicle_type) { setError("Le type de bien est obligatoire."); return; }
    if (!form.category) { setError("La catégorie est obligatoire."); return; }
    if (!form.internal_ref || !form.internal_ref.trim()) { setError("Le numéro d'identification est obligatoire."); return; }
    if (!form.brand || !form.brand.trim()) { setError("La marque est obligatoire."); return; }
    if (!form.model || !form.model.trim()) { setError("Le modèle est obligatoire."); return; }
    if (locations.length > 0 && (!form.location_ids || form.location_ids.length === 0)) { setError("Sélectionnez au moins une agence."); return; }
    // Phase 8 — refus des valeurs numériques négatives (quantités, montants, distances…)
    const nonNeg = ["seats", "doors", "year", "fiscal_power", "engine_power", "mileage", "fuel_level", "purchase_price", "estimated_value", "insurance_monthly", "maintenance_estimated"];
    for (const k of nonNeg) {
      if (form[k] !== "" && form[k] != null && Number(form[k]) < 0) {
        setError("Les valeurs numériques ne peuvent pas être négatives.");
        return;
      }
    }
    setSaving(true);
    try {
      // Sanitize : convertit les strings numériques en number, "" en null pour Optional
      const numericFields = ["seats", "doors", "year", "fiscal_power", "engine_power", "mileage", "max_mileage", "fuel_level", "tank_capacity", "autonomy", "co2_emissions", "purchase_price", "estimated_value", "insurance_monthly", "maintenance_estimated", "deposit", "deductible", "km_included_daily", "price_extra_km"];
      const payload = { ...form };
      numericFields.forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      payload.seats = parseInt(form.seats) || 5;
      if (payload.first_registration_date === "") payload.first_registration_date = null;
      if (payload.purchase_date === "") payload.purchase_date = null;
      if (payload.crit_air === "") payload.crit_air = null;
      // Multi-agences : location_ids est la source ; location_id (principal) = première agence (rétro-compat).
      payload.location_ids = form.location_ids || [];
      payload.location_id = payload.location_ids.length ? payload.location_ids[0] : null;
      if (editing) {
        await api.patch(`/vendor/vehicles/${editing.id}`, payload);
        toast.success("Véhicule mis à jour");
      } else {
        await api.post("/vendor/vehicles", payload);
        toast.success("Bien créé et ajouté à votre flotte");
      }
      setOpen(false);
      load();
      emitDataChanged("vehicles");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const veh = vehicles.find((x) => x.id === id);
    // Récupère les dépendances réelles pour la fenêtre enrichie
    let imp;
    try {
      imp = (await api.get(`/vendor/vehicles/${id}/deletion-impact`)).data;
    } catch {
      toast.error("Impossible de calculer les dépendances du véhicule");
      return;
    }
    const lines = [
      `${imp.bookings} réservation${imp.bookings > 1 ? "s" : ""}${imp.future_bookings ? `, dont ${imp.future_bookings} à venir` : ""}`,
      `${imp.issues} signalement${imp.issues > 1 ? "s" : ""}`,
      `${imp.claims} sinistre${imp.claims > 1 ? "s" : ""}`,
      `${imp.fines} contravention${imp.fines > 1 ? "s" : ""}`,
      `${imp.interventions} intervention${imp.interventions > 1 ? "s" : ""}`,
    ].map((l) => `• ${l}`).join("\n");

    const blocked = !imp.can_delete;
    const baseMsg = `« ${veh?.name || "Ce véhicule"} » possède actuellement :\n${lines}\n\n`;
    const msg = blocked
      ? baseMsg + `⚠️ Ce véhicule est associé à ${imp.blocking_bookings} réservation(s) en cours ou à venir. Pour le supprimer, réattribuez-les à un autre véhicule ou résolvez les engagements concernés.\nVous pouvez l'archiver : il sera retiré de la flotte active sans perdre aucune donnée.`
      : baseMsg + `La suppression définitive effacera aussi les événements dépendants (signalements, sinistres, contraventions, interventions). Les réservations historiques sont conservées. Cette opération est IRRÉVERSIBLE.`;

    const choice = await confirmAction({
      title: "Supprimer ce véhicule ?",
      message: msg,
      variant: blocked ? "warning" : "danger",
      confirmLabel: "Supprimer définitivement",
      secondaryLabel: veh?.archived ? undefined : "Archiver",
      confirmDisabled: blocked,
      requireText: blocked ? undefined : "SUPPRIMER",
    });
    if (choice === "secondary") {
      try { await api.post(`/vendor/vehicles/${id}/archive`); toast.success("Véhicule archivé"); } catch { toast.error("Archivage impossible"); }
      load();
      emitDataChanged("vehicles");
      return;
    }
    if (choice !== true) return;
    try {
      await api.delete(`/vendor/vehicles/${id}`);
      toast.success("Véhicule supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
    load();
    emitDataChanged("vehicles");
  };

  const archiveVehicle = async (v) => {
    const verb = v.archived ? "Restaurer" : "Archiver";
    if (!(await confirmAction({
      title: `${verb} « ${v.name} » ?`,
      message: v.archived
        ? "Le véhicule sera restauré dans votre flotte active et pourra de nouveau être réservé."
        : "Le véhicule sera retiré de la flotte active : il ne pourra plus être réservé, mais son historique est conservé.",
      confirmLabel: `Confirmer : ${verb.toLowerCase()}`,
      variant: "warning",
    }))) return;
    const { data } = await api.post(`/vendor/vehicles/${v.id}/archive`);
    toast.success(data.archived ? "Véhicule archivé" : "Véhicule restauré");
    load();
    emitDataChanged("vehicles");
  };

  const toggleStatus = async (v) => {
    const next = v.status === "active" ? "inactive" : "active";
    await api.patch(`/vendor/vehicles/${v.id}`, { status: next });
    load();
    emitDataChanged("vehicles");
  };

  const filteredVehicles = vehicles;  // backend gère déjà les filtres

  // Barre d'outils unifiée — helpers de filtres (Groupe B)
  const catOptions = (typeF === "all" ? CAT_OBJS : CAT_OBJS.filter((c) => c.type === typeF)).map((c) => ({ value: c.value, label: c.label }));
  // Résolution du nom métier d'une catégorie (jamais l'identifiant technique « cat_… »)
  const catLabelMap = Object.fromEntries((CAT_OBJS || []).map((c) => [c.value, c.label || c.value]));
  const catLabel = (val) => (val == null || val === "" ? "" : (catLabelMap[val] || val));
  const changeType = (v) => { setTypeF(v); setCategoryF("all"); };
  const hasFilter = statusF !== "all" || typeF !== "all" || categoryF !== "all" || fuelF !== "all" || transF !== "all" || agencyF !== "all" || !!search;
  const resetFilters = () => { setSearch(""); setStatusF("all"); setTypeF("all"); setCategoryF("all"); setFuelF("all"); setTransF("all"); setAgencyF("all"); setSortBy("created_desc"); };
  const canCreate = can("vehicles", "create");
  const Divider = () => <span className="h-6 w-px bg-slate-200 mx-0.5 hidden lg:block" aria-hidden />;

  const content = (
    <div className={embedded ? "p-4 sm:p-6" : "w-full p-6 sm:p-8"}>
      {!open && (
      <>
        <TabToolbar
          title="Véhicules"
          count={vehicles.length}
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Rechercher un véhicule..."
          searchTestid="search-vehicles"
          permissionModule="vehicles"
          filtersActive={hasFilter ? 1 : 0}
          onClearFilters={hasFilter ? resetFilters : null}
          filters={
            !loading && (vehicles.length > 0 || hasFilter) ? (
              <>
                {VTYPES.length > 0 && (
                  <FilterDropdown label="Type" value={typeF} onChange={changeType} options={[{ value: "all", label: "Tous les types" }, ...VTYPES.map((t) => ({ value: t.value, label: t.label }))]} testid="filter-type" />
                )}
                <FilterDropdown label="Catégorie" value={categoryF} onChange={setCategoryF} options={[{ value: "all", label: "Toutes catégories" }, ...catOptions]} testid="filter-category" />
                {locations.length > 0 && (
                  <FilterDropdown label="Agence" value={agencyF} onChange={setAgencyF} options={[{ value: "all", label: "Toutes les agences" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]} testid="filter-agency" />
                )}
                {hasFilter && (
                  <button onClick={resetFilters} className="inline-flex items-center gap-1 px-3 h-10 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 hover:border-slate-400 bg-white shrink-0" data-testid="reset-filters">
                    <RotateCcw className="h-3 w-3" /> Réinitialiser
                  </button>
                )}
                <span className="text-xs text-slate-400 ml-1 shrink-0" data-testid="results-count">
                  {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""}{hasFilter ? " · filtres actifs" : ""}
                </span>
              </>
            ) : null
          }
          viewToggle={(!loading && (vehicles.length > 0 || hasFilter)) ? <ViewToggle value={viewMode} onChange={setViewMode} testid="view" items={[{ key: "cards", icon: LayoutGrid, label: "Vignettes" }, { key: "table", icon: List, label: "Liste" }]} /> : null}
          addLabel="Ajouter un véhicule"
          onAdd={canCreate ? openCreate : null}
          addTestid="open-vehicle-form"
          extra={
            <>
              <Link to="/vendor/pilotage?tab=vehicles&view=events" className="hidden lg:inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-10 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors shrink-0" data-testid="fleet-events-link">
                <ShieldAlert className="h-3.5 w-3.5" /> <span className="hidden xl:inline">Événements</span>
              </Link>
              <Link to="/vendor/parametrages?section=fleet&from=fleet" className="shrink-0 hidden lg:inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-10 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors" data-testid="fleet-settings-link">
                <Settings className="h-3.5 w-3.5" /> <span className="hidden xl:inline">Paramètres</span>
              </Link>
              <CsvMenu exportPath="/vendor/vehicles/export" exportFilename="vehicules.csv" importPath="/vendor/vehicles/import" onDone={load} testid="vehicles-csv-menu" />
            </>
          }
        />

        <div className="mt-6">
          {loading ? (
            viewMode === "table"
              ? <SkeletonTableRows rows={7} cols={7} testid="vehicles-skeleton-rows" />
              : <SkeletonCards count={6} testid="vehicles-skeleton-cards" />
          ) : vehicles.length === 0 ? (
            hasFilter ? (
              <EmptyState
                icon={Search}
                title="Aucun résultat"
                description="Aucun véhicule ne correspond à votre recherche ou à vos filtres."
                actionLabel="Réinitialiser les filtres"
                onAction={resetFilters}
                testid="vehicles-no-results"
              />
            ) : (
              <EmptyState
                icon={CarFront}
                title="Aucun véhicule pour le moment"
                description="Commencez par ajouter votre premier véhicule pour construire votre flotte."
                actionLabel="Ajouter un véhicule"
                onAction={openCreate}
                testid="vehicles-empty"
              />
            )
          ) : viewMode === "table" ? (
            <VehiclesTable vehicles={filteredVehicles} statsMap={statsMap} STATUSES={STATUSES} catLabel={catLabel} onEdit={openEdit} onToggle={toggleStatus} onRemove={remove} onArchive={archiveVehicle} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredVehicles.map((v) => {
                const a = AVAIL[v.availability] || AVAIL.available;
                return (
                <div key={v.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden card-hover" data-testid={`vendor-vehicle-${v.id}`}>
                  <div className="relative aspect-[16/10] bg-slate-100">
                    <img src={v.photos?.[0] || "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?w=800&q=80"} alt="" className="h-full w-full object-cover" />
                    <div className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${a.color}`} data-testid={`avail-${v.id}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`} /> {a.label}
                    </div>
                    {v.license_plate && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/90 backdrop-blur text-xs font-mono text-slate-900">
                        <Hash className="h-3 w-3" /> {v.license_plate}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-slate-500">{catLabel(v.category)}</div>
                        <div className="font-heading font-medium text-slate-900 truncate" data-testid={`card-name-${v.id}`}>{vehiclePrimaryLabel(v)}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {v.city}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Statut</div>
                        <div className="text-xs font-semibold text-slate-700 capitalize">{v.availability || v.status}</div>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Revenu net</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{formatEUR(statsMap[v.id]?.total_net || 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Occupation</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{statsMap[v.id]?.occupation_rate_90d ?? (v.occupancy_rate || 0)}%</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Locations</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{statsMap[v.id]?.bookings_count || 0}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">€ / jour loué</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{formatEUR(statsMap[v.id]?.revenue_per_day || 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Charge / mois</div>
                        <div className="font-semibold text-slate-900 mt-0.5">{formatEUR(statsMap[v.id]?.monthly_costs || 0)}</div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Dernière</div>
                        <div className="font-semibold text-slate-900 mt-0.5 truncate">{statsMap[v.id]?.last_booking ? formatDateFR(statsMap[v.id].last_booking) : "—"}</div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Link
                        to={`/vendor/pilotage?tab=vehicles&vehicle=${v.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full btn-primary text-xs font-semibold"
                        data-testid={`view-${v.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" /> Voir la fiche
                      </Link>
                      <button
                        onClick={() => openEdit(v)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium hover:border-slate-900"
                        data-testid={`edit-${v.id}`}
                        title="Modification rapide"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => toggleStatus(v)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium hover:border-slate-900"
                        data-testid={`toggle-${v.id}`}
                        title={v.status === "active" ? "Désactiver" : "Activer"}
                      >
                        <Power className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => archiveVehicle(v)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium hover:border-slate-900"
                        data-testid={`archive-${v.id}`}
                        title={v.archived ? "Restaurer" : "Archiver"}
                      >
                        {v.archived ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        onClick={() => remove(v.id)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50"
                        data-testid={`delete-${v.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </>
      )}

      {/* Formulaire pleine page (sous l'onglet) */}
      {open && (
        <div data-testid="vehicle-form-page">
          <div className="w-full">
            <button
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-4"
              data-testid="vehicle-form-back"
            >
              ← Retour aux véhicules
            </button>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-500">{editing ? "Fiche véhicule" : "Nouveau"}</div>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight mt-1">{editing ? `Modifier — ${editing.name || editing.brand + " " + editing.model}` : "Nouveau véhicule"}</h2>
              </div>
            </div>

            <form onSubmit={save} noValidate className="p-6 space-y-6">
              {/* Section 1 — Informations générales */}
              <Section title="Informations générales" testid="section-general">
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField
                    label="Type de bien"
                    required
                    value={form.vehicle_type || ""}
                    onChange={(v) => {
                      if (v === "__create_type__") { setQuickCreate({ open: true, kind: "type", value: "" }); return; }
                      if (form.id && v !== form.vehicle_type && !window.confirm("Changer le type de bien adaptera les champs affichés à ce nouveau type. Les valeurs déjà saisies sont conservées (non supprimées). Continuer ?")) return;
                      const cats = CAT_OBJS.filter((c) => c.type === v);
                      const keep = cats.some((c) => c.value === form.category);
                      setForm({ ...form, vehicle_type: v, category: keep ? form.category : (cats[0]?.value || "") });
                    }}
                    options={[
                      ...(VTYPES.length ? VTYPES : (settingsLoaded ? [{ value: "", label: "— Aucun type de bien" }] : [{ value: "car", label: "Voiture" }])),
                      { value: "__create_type__", label: "＋ Créer un type de bien" },
                    ]}
                    testid="v-vehicle-type"
                  />
                  <SelectField
                    label="Catégorie"
                    required
                    value={form.category}
                    onChange={(v) => {
                      if (v === "__create_category__") {
                        if (!form.vehicle_type) { toast.error("Choisissez d'abord un type de bien"); return; }
                        setQuickCreate({ open: true, kind: "category", value: "" });
                        return;
                      }
                      setForm({ ...form, category: v });
                    }}
                    options={(() => {
                      const cats = CAT_OBJS.filter((c) => c.type === (form.vehicle_type || "car"));
                      const base = cats.length
                        ? cats.map((c) => ({ value: c.value, label: c.label }))
                        : (!settingsLoaded ? CATS.map((x) => (typeof x === "string" ? { value: x, label: x } : x))
                          : [{ value: "", label: `— Aucune catégorie pour ${VTYPES.find((t) => t.value === form.vehicle_type)?.label || "ce type"}` }]);
                      return [...base, { value: "__create_category__", label: "＋ Créer une catégorie" }];
                    })()}
                    testid="v-category"
                  />
                  <div className="sm:col-span-2">
                    <Field label="Numéro d'identification" required value={form.internal_ref} onChange={(v) => setForm({ ...form, internal_ref: v })} testid="v-internal-ref" info="Ce numéro sera affiché lors de la sélection du véhicule dans une réservation ou un devis." infoTestid="v-internal-ref-info" />
                    <p className="mt-1 text-[11px] text-slate-400 leading-relaxed" data-testid="v-internal-ref-hint">
                      Identifiez librement votre bien. Pour une voiture, nous recommandons la plaque d'immatriculation. Pour un vélo ou un autre équipement, vous pouvez attribuer le numéro de votre choix.
                    </p>
                  </div>
                  <Field label="Marque" required value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testid="v-brand" />
                  <Field label="Modèle" required value={form.model} onChange={(v) => setForm({ ...form, model: v })} testid="v-model" />
                  {locations.length > 0 ? (
                    <AgencyMultiSelect
                      locations={locations}
                      value={form.location_ids}
                      onChange={(ids) => setForm({ ...form, location_ids: ids })}
                    />
                  ) : (
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Agences<span className="text-red-500 ml-0.5">*</span></label>
                      <div className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5" data-testid="v-location-empty">
                        Créez d'abord une agence dans Flotte → Agences pour pouvoir rattacher ce bien.
                      </div>
                    </div>
                  )}
                </div>
              </Section>

              {/* Sections dynamiques pilotées par la configuration de champs du Type sélectionné */}
              <DynamicVehicleFields
                typeConfig={VTYPES_FULL.find((t) => t.value === form.vehicle_type)?.fields}
                fieldCatalog={fieldCatalog}
                form={form}
                setForm={setForm}
                vehicleId={form.id}
              />

              {/* Description et photos */}
              <Section title="Description et photos" testid="section-public" defaultOpen={false}>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600" data-testid="v-description" />
                <div className="mt-4">
                  <PhotoManager
                    vehicleId={editing?.id}
                    photos={form.photos}
                    onChange={(photos) => setForm({ ...form, photos })}
                  />
                </div>
              </Section>

              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="v-error">{error}</div>}

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-slate-100">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 px-5 py-3 rounded-xl border border-slate-200 text-sm font-medium hover:border-slate-900">
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-5 py-3 rounded-full btn-primary text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="save-vehicle-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing ? "Enregistrer" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {quickCreate.open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="quick-create-modal">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => !quickSaving && setQuickCreate({ open: false, kind: "type", value: "" })} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5">
            <div className="font-heading text-base font-semibold text-slate-900 mb-1">
              {quickCreate.kind === "type" ? "Nouveau type de bien" : "Nouvelle catégorie"}
            </div>
            <div className="text-xs text-slate-500 mb-4">
              {quickCreate.kind === "type"
                ? "Créez un type sans quitter le formulaire. Il sera sélectionné automatiquement."
                : `Catégorie pour « ${VTYPES.find((t) => t.value === form.vehicle_type)?.label || "ce type"} ». Elle sera sélectionnée automatiquement.`}
            </div>
            <input
              autoFocus
              value={quickCreate.value}
              onChange={(e) => setQuickCreate((q) => ({ ...q, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveQuickCreate(); } }}
              placeholder={quickCreate.kind === "type" ? "Ex. Jet-ski" : "Ex. Sport"}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600"
              data-testid="quick-create-input"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button type="button" onClick={() => setQuickCreate({ open: false, kind: "type", value: "" })} disabled={quickSaving}
                className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="quick-create-cancel">
                Annuler
              </button>
              <button type="button" onClick={saveQuickCreate} disabled={quickSaving || !quickCreate.value.trim()}
                className="h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2" data-testid="quick-create-save">
                {quickSaving && <Loader2 className="h-4 w-4 animate-spin" />} Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return embedded ? content : <VendorLayout>{content}</VendorLayout>;
}

function Section({ title, children, testid, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/40" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 rounded-t-2xl">
        <span className="font-heading text-base font-medium text-slate-900">{title}</span>
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
        </span>
      </button>
      {open && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, min, info, infoTestid }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}{info && <InfoTip text={info} testid={infoTestid} />}</label>
      <input
        type={type}
        value={value}
        min={type === "number" ? (min ?? 0) : undefined}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600"
        data-testid={testid}
      />
    </div>
  );
}

// Infobulle discrète (ⓘ) : survol sur ordinateur + clic/toucher sur mobile.
function InfoTip({ text, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex group align-middle ml-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        aria-label={text}
        title={text}
        className="text-slate-400 hover:text-violet-600 transition-colors"
        data-testid={testid}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      <span
        className={`${open ? "block" : "hidden"} group-hover:block absolute left-0 top-6 z-50 w-56 rounded-lg bg-slate-900 text-white text-[11px] font-medium normal-case tracking-normal leading-snug p-2 shadow-lg`}
        data-testid={testid ? `${testid}-text` : undefined}
      >
        {text}
      </span>
    </span>
  );
}

function SelectField({ label, value, onChange, options, testid, required }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 bg-white"
        data-testid={testid}
      >
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lab = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{lab}</option>;
        })}
      </select>
    </div>
  );
}

function DynamicVehicleFields({ typeConfig, fieldCatalog, form, setForm, vehicleId }) {
  // Marque/Modèle sont désormais affichés dans « Informations générales » :
  // on les exclut ici pour ne pas les dupliquer (sans toucher au paramétrage des champs).
  const fields = orderedActiveFields(typeConfig, fieldCatalog)
    .filter((f) => !(f.kind === "catalog" && (f.key === "brand" || f.key === "model")));
  if (!fields.length) return null;

  const getV = (key) => (key.startsWith("equip.") ? !!form.equipment?.[key.slice(6)] : form[key]);
  const setV = (key, v) => key.startsWith("equip.")
    ? setForm({ ...form, equipment: { ...(form.equipment || {}), [key.slice(6)]: v } })
    : setForm({ ...form, [key]: v });
  const getC = (id) => form.custom_fields?.[id];
  const setC = (id, v) => setForm({ ...form, custom_fields: { ...(form.custom_fields || {}), [id]: v } });

  const renderInput = (f, value, onChange, testid) => {
    const input = f.input;
    if (input === "textarea") return <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm" data-testid={testid} />;
    if (input === "checkbox") return <label className="mt-1.5 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-violet-600" data-testid={testid} /><span className="text-slate-600">Oui</span></label>;
    if (input === "select") return <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white" data-testid={testid}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
    if (input === "tags") return <input value={Array.isArray(value) ? value.join(", ") : (value || "")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="étiquette1, étiquette2" className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" data-testid={testid} />;
    if (input === "slider") {
      const mn = f.min ?? 0, mx = f.max ?? 100, st = f.step ?? 1;
      return <div className="mt-2"><input type="range" min={mn} max={mx} step={st} value={value || mn} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-violet-600" data-testid={testid} /><div className="text-xs text-slate-500 mt-1">{value ?? mn}</div></div>;
    }
    if (input === "images" || input === "files") return <CustomAssetField field={f} vehicleId={vehicleId} value={value} onChange={onChange} testid={testid} />;
    return <input type={input === "number" ? "number" : input === "date" ? "date" : input === "url" ? "url" : "text"} min={input === "number" ? 0 : undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm" data-testid={testid} />;
  };

  return (
    <Section title="Caractéristiques du bien" testid="section-dynamic" defaultOpen={false}>
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => {
          const isCustom = f.kind === "custom";
          const value = isCustom ? getC(f.id) : getV(f.key);
          const onChange = isCustom ? (v) => setC(f.id, v) : (v) => setV(f.key, v);
          const testid = isCustom ? `v-custom-${f.id}` : `v-field-${f.key}`;
          const wide = f.input === "textarea" || f.input === "images" || f.input === "files";
          return (
            <div key={f.token} className={wide ? "sm:col-span-2" : ""}>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{f.label}{f.key === "name" && <InfoTip text="Ce nom sera affiché lors de la sélection du véhicule dans une réservation ou un devis." testid="v-name-info" />}</label>
              {renderInput(f, value, onChange, testid)}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// Champ personnalisé Images / Fichiers : upload réel (après création du bien).
function CustomAssetField({ field, vehicleId, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const assets = Array.isArray(value) ? value : [];
  const isImages = field.input === "images";
  const kind = isImages ? "images" : "files";

  const handleFiles = async (files) => {
    if (!vehicleId) { toast.error("Enregistrez d'abord le bien pour ajouter des fichiers."); return; }
    if (!files?.length) return;
    setBusy(true);
    let next = assets;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/vendor/vehicles/${vehicleId}/custom-fields/${field.id}/files?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        next = data.assets;
      }
      onChange(next);
      toast.success("Ajouté");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'upload");
    } finally { setBusy(false); }
  };

  const removeAsset = async (a) => {
    if (!vehicleId) return;
    try {
      const { data } = await api.delete(`/vendor/vehicles/${vehicleId}/custom-fields/${field.id}/files/${a.id}`);
      onChange(data.assets);
      toast.success("Supprimé");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
  };

  if (!vehicleId) {
    return <div className="mt-1.5 text-xs text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-3 py-2.5" data-testid={testid}>Ajout de {isImages ? "photos" : "fichiers"} disponible après enregistrement du bien.</div>;
  }
  return (
    <div className="mt-1.5" data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {assets.map((a) => (
          <div key={a.id} className="relative group" data-testid={`${testid}-asset-${a.id}`}>
            {isImages ? (
              <img src={photoUrl(a.path)} alt={a.filename} className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
            ) : (
              <a href={photoUrl(a.path)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:border-violet-300 max-w-[180px]"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.filename}</span></a>
            )}
            <button type="button" onClick={() => removeAsset(a)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-remove-${a.id}`}>×</button>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-violet-300 cursor-pointer" data-testid={`${testid}-upload`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Ajouter {isImages ? "une image" : "un fichier"}
        <input type="file" multiple accept={isImages ? "image/*" : undefined} className="hidden" onChange={(e) => { handleFiles(Array.from(e.target.files || [])); e.target.value = ""; }} disabled={busy} />
      </label>
    </div>
  );
}

function PhotoManager({ vehicleId, photos, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!vehicleId) {
      toast.error("Créez d'abord le véhicule pour ajouter des photos");
      return;
    }
    if (!files?.length) return;
    setUploading(true);
    let next = [...photos];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/vendor/vehicles/${vehicleId}/photos`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        next = data.photos;
      }
      onChange(next);
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} ajoutée${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (p) => {
    if (!vehicleId) return;
    try {
      const { data } = await api.delete(
        `/vendor/vehicles/${vehicleId}/photos`,
        { params: { photo: p } },
      );
      onChange(data.photos);
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const setAsMain = async (p) => {
    if (!vehicleId) return;
    const next = [p, ...photos.filter((x) => x !== p)];
    try {
      const { data } = await api.put(
        `/vendor/vehicles/${vehicleId}/photos`,
        { photos: next },
      );
      onChange(data.photos);
      toast.success("Photo principale mise à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="photo-manager">
      <div className="flex items-end justify-between">
        <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
          Photos
        </label>
        <span className="text-[10px] text-slate-400">JPG, PNG, WebP · max 10 Mo</span>
      </div>

      {!vehicleId ? (
        <div className="mt-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500">
          Créez d'abord le véhicule pour pouvoir ajouter ses photos.
        </div>
      ) : (
        <>
          {/* Grid */}
          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div
                  key={p}
                  className={`group relative aspect-square rounded-xl overflow-hidden border-2 ${
                    i === 0 ? "border-violet-500" : "border-slate-200"
                  }`}
                  data-testid={`vehicle-photo-${i}`}
                >
                  <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                  {i === 0 && (
                    <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-semibold shadow">
                      <Star className="h-2.5 w-2.5 fill-white" /> Principale
                    </div>
                  )}
                  <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {i !== 0 && (
                      <button
                        type="button"
                        onClick={() => setAsMain(p)}
                        className="h-8 w-8 rounded-full bg-white text-slate-900 flex items-center justify-center hover:bg-violet-50"
                        title="Définir comme principale"
                        data-testid={`set-main-${i}`}
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p)}
                      className="h-8 w-8 rounded-full bg-white text-red-600 flex items-center justify-center hover:bg-red-50"
                      title="Supprimer"
                      data-testid={`delete-photo-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload zone */}
          <label
            className={`mt-3 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
              uploading ? "border-violet-300 bg-violet-50/40" : "border-slate-200 hover:border-violet-400 hover:bg-violet-50/30"
            }`}
            data-testid="photo-upload-zone"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
            ) : (
              <Upload className="h-5 w-5 text-slate-400" />
            )}
            <span className="text-xs font-medium text-slate-700">
              {uploading ? "Upload en cours…" : "Cliquez pour ajouter des photos"}
            </span>
            <span className="text-[10px] text-slate-400">
              {photos.length === 0
                ? "La 1ère photo sera utilisée comme photo principale"
                : "Plusieurs fichiers possibles"}
            </span>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                handleFiles(Array.from(e.target.files || []));
                e.target.value = "";
              }}
              data-testid="photo-upload-input"
            />
          </label>
        </>
      )}
    </div>
  );
}

// ─── Filter bar (Phase 4) ──────────────────────────────────────────────
function FilterBar({
  search, setSearch, statusF, setStatusF, typeF, setTypeF, categoryF, setCategoryF,
  fuelF, setFuelF, transF, setTransF, sortBy, setSortBy,
  viewMode, setViewMode, CATS, STATUSES, VTYPES = [], CAT_OBJS = [], resultCount,
}) {
  const FUELS = ["Essence", "Diesel", "Hybride", "Électrique"];
  const TRANS = ["Automatique", "Manuelle"];
  const SORTS = [
    { value: "created_desc", label: "Plus récents" },
    { value: "created_asc", label: "Plus anciens" },
    { value: "name_asc", label: "Nom A-Z" },
    { value: "mileage_desc", label: "Kilométrage ↓" },
    { value: "mileage_asc", label: "Kilométrage ↑" },
  ];
  const hasFilter = statusF !== "all" || typeF !== "all" || categoryF !== "all" || fuelF !== "all" || transF !== "all" || !!search;
  const reset = () => {
    setSearch(""); setStatusF("all"); setTypeF("all"); setCategoryF("all"); setFuelF("all"); setTransF("all"); setSortBy("created_desc");
  };
  const catOptions = (typeF === "all"
    ? CAT_OBJS
    : CAT_OBJS.filter((c) => c.type === typeF)
  ).map((c) => ({ value: c.value, label: c.label }));
  const changeType = (v) => { setTypeF(v); setCategoryF("all"); };
  return (
    <div className="mt-6 space-y-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {VTYPES.length > 0 && (
            <FilterDropdown label="Type" value={typeF} onChange={changeType} options={[{ value: "all", label: "Tous les types" }, ...VTYPES.map((t) => ({ value: t.value, label: t.label }))]} testid="filter-type" />
          )}
          <FilterDropdown label="Catégorie" value={categoryF} onChange={setCategoryF} options={[{ value: "all", label: "Toutes catégories" }, ...catOptions]} testid="filter-category" />
          {hasFilter && (
            <button
              onClick={reset}
              className="inline-flex items-center gap-1 px-3 h-10 rounded-xl border border-slate-200 text-xs font-medium text-slate-600 hover:border-slate-400 bg-white"
              data-testid="reset-filters"
            >
              <RotateCcw className="h-3 w-3" /> Réinitialiser
            </button>
          )}
        </div>
        <div className="ml-auto inline-flex items-center gap-2 shrink-0">
          <Link
            to="/vendor/pilotage?tab=vehicles&view=events"
            className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors"
            data-testid="fleet-events-link"
          >
            <ShieldAlert className="h-3.5 w-3.5" /> Événements flotte
          </Link>
          <ViewToggle
            value={viewMode}
            onChange={setViewMode}
            testid="view"
            items={[
              { key: "cards", icon: LayoutGrid, label: "Vignettes" },
              { key: "table", icon: List, label: "Liste" },
            ]}
          />
        </div>
      </div>
      <div className="text-xs text-slate-500" data-testid="results-count">
        {resultCount} véhicule{resultCount > 1 ? "s" : ""}
        {hasFilter && " · filtres actifs"}
      </div>
    </div>
  );
}

function VehiclesTable({ vehicles, statsMap, STATUSES, catLabel = (x) => x, onEdit, onToggle, onRemove, onArchive }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="vehicles-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Véhicule</th>
              <th className="px-4 py-3 font-semibold hidden md:table-cell">Catégorie</th>
              <th className="px-4 py-3 font-semibold hidden lg:table-cell">Plaque</th>
              <th className="px-4 py-3 font-semibold">Statut</th>
              <th className="px-4 py-3 font-semibold text-right hidden md:table-cell">Revenu net</th>
              <th className="px-4 py-3 font-semibold text-right hidden lg:table-cell">Occup. 90j</th>
              <th className="px-4 py-3 font-semibold text-right hidden xl:table-cell">Locations</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => {
              const s = statsMap[v.id] || {};
              return (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-vehicle-${v.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        <img src={photoUrl(v.photos?.[0])} alt="" className="h-full w-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <Link to={`/vendor/pilotage?tab=vehicles&vehicle=${v.id}`} className="font-medium text-slate-900 truncate hover:text-violet-700" data-testid={`row-name-${v.id}`}>
                          {vehiclePrimaryLabel(v)}
                        </Link>
                        <div className="text-xs text-slate-500 truncate">{v.city}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-600">{catLabel(v.category)}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {v.license_plate ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-xs font-mono text-slate-700">
                        <Hash className="h-3 w-3" /> {v.license_plate}
                      </span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadgeClasses(v.status, STATUSES)}`} data-testid={`row-status-${v.id}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {statusLabelFor(v.status, STATUSES)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-slate-700">{formatEUR(s.total_net || 0)}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-slate-700">{s.occupation_rate_90d ?? 0}%</td>
                  <td className="px-4 py-3 text-right hidden xl:table-cell text-slate-700">{s.bookings_count || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/vendor/pilotage?tab=vehicles&vehicle=${v.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-600 text-white text-[10px] font-semibold hover:bg-violet-700" data-testid={`row-view-${v.id}`}>
                        <Eye className="h-3 w-3" /> Fiche
                      </Link>
                      <button onClick={() => onEdit(v)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-600 flex items-center justify-center" title="Modifier" data-testid={`row-edit-${v.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onToggle(v)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-600 flex items-center justify-center" title="Activer/Désactiver" data-testid={`row-toggle-${v.id}`}>
                        <Power className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onArchive(v)} className="h-7 w-7 rounded-md hover:bg-slate-100 text-slate-600 flex items-center justify-center" title={v.archived ? "Restaurer" : "Archiver"} data-testid={`row-archive-${v.id}`}>
                        {v.archived ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => onRemove(v.id)} className="h-7 w-7 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center" title="Supprimer" data-testid={`row-delete-${v.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
